import {
	ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH,
	APPOINTMENT_PLACEHOLDERS_BASE_PATH,
} from '@zyro-inc/cdn-builder-placeholders/constants';
import {
	EcommerceProduct,
	EcommerceProductType,
	ECOMMERCE_SHIPPING_PROVIDER_TYPE,
} from '@zyro-inc/site-modules/types';
import { SortingOptions } from '@hostinger/builder-schema-validator/schema/schemaTypes';

export const DEFAULT_EMPTY_PRODUCT_VALUE: EcommerceProduct = {
	// this product object structure is aligned with the one returned from backend
	id: -1,
	title: 'Product name',
	subtitle: null,
	description: `This is a sample product description. You can use it to describe your product,\
		from its size, weight, and color to other characteristics like material, and so on.
		\nMake sure you highlight the best qualities and the most important functions\
		that the product has. Make your customers want it and tell them how the product\
		could help make their life easier or simply more beautiful. After you have added\
		your product description in the store settings, it will appear here automatically`,
	images: [],
	options: [],
	product_collections: [],
	ribbon_text: null,
	site_product_selection: null,
	type: {
		value: EcommerceProductType.PHYSICAL,
	},
	thumbnail: null,
	variants: [
		{
			id: '',
			manage_inventory: false,
			prices: [
				{
					amount: 0,
					currency_code: 'USD',
					sale_amount: null,
					currency: {
						code: 'USD',
						decimal_digits: 2,
						template: '$$1',
						name: 'US Dollar',
						symbol: '$',
						symbol_native: '$',
						name_plural: 'US dollars',
						rounding: 0,
						min_amount: 0,
					},
				},
			],
			options: [],
		},
	],
	seo_settings: {},
	purchasable: true,
	additional_info: [],
	updated_at: '',
};

export const DEFAULT_EMPTY_APPOINTMENT_VALUE: EcommerceProduct = {
	// this product object structure is aligned with the one returned from backend
	id: -1,
	title: 'Appointment name',
	subtitle: null,
	description: `This is a sample appointment description. You can use it to describe your appointment,\
		from its size, weight, and color to other characteristics like material, and so on.
		\nMake sure you highlight the best qualities and the most important functions\
		that the product has. Make your customers want it and tell them how the product\
		could help make their life easier or simply more beautiful. After you have added\
		your product description in the store settings, it will appear here automatically`,
	images: [],
	options: [],
	product_collections: [],
	ribbon_text: null,
	site_product_selection: null,
	type: {
		value: EcommerceProductType.BOOKING,
	},
	thumbnail: null,
	variants: [
		{
			id: '',
			manage_inventory: false,
			prices: [
				{
					amount: 0,
					currency_code: 'USD',
					sale_amount: null,
					currency: {
						code: 'USD',
						decimal_digits: 2,
						template: '$$1',
						name: 'US Dollar',
						symbol: '$',
						symbol_native: '$',
						name_plural: 'US dollars',
						rounding: 0,
						min_amount: 0,
					},
				},
			],
			options: [],
			booking_event: {
				id: 'demo_booking_event_id',
				length: 2700000,
				length_unit: 'minutes',
				minimum_booking_notice: null,
				minimum_booking_notice_unit: 'minutes',
				buffer_time_before: null,
				buffer_time_after: null,
				location: 'Location name',
				schedule_id: 'demo_schedule_id',
			},
		},
	],
	seo_settings: {},
	purchasable: true,
	additional_info: [],
	updated_at: '',
};

export const DEMO_APPOINTMENTS = [
	{
		...DEFAULT_EMPTY_APPOINTMENT_VALUE,
		title: 'Intro language tutoring session',
		images: [
			{
				url: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/language-tutoring.png`,
			},
		],
		thumbnail: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/language-tutoring.png`,
		subtitle: 'Practice, learn, and grow your language skills',
		description: 'An engaging one-on-one language lesson tailored to your level and goals. Whether you’re preparing for travel, improving pronunciation, or expanding vocabulary, each session is designed to make learning enjoyable and effective.',
		variants: [
			{
				id: '',
				manage_inventory: false,
				title: 'Intro language tutoring session',
				prices: [
					{
						amount: 0,
						currency_code: 'USD',
						sale_amount: null,
						currency: {
							code: 'USD',
							decimal_digits: 2,
							template: '$$1',
							name: 'US Dollar',
							symbol: '$',
							symbol_native: '$',
							name_plural: 'US dollars',
							rounding: 0,
							min_amount: 0,
						},
					},
				],
				options: [],
				booking_event: {
					id: '',
					buffer_time_before: null,
					buffer_time_after: null,
					length: 2700000,
					length_unit: 'minutes',
					location: '45 Oakwood Avenue, Brighton',
					minimum_booking_notice: 600000,
					minimum_booking_notice_unit: 'minutes',
				},
			},
		],
	},
	{
		...DEFAULT_EMPTY_APPOINTMENT_VALUE,
		title: 'Individual coaching session',
		images: [
			{
				url: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/life-coach.png`,
			},
		],
		thumbnail: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/life-coach.png`,
		subtitle: 'Achieve your personal or professional goals with tailored guidance',
		description: 'A focused one-on-one session designed to help you gain clarity, set actionable goals, and overcome challenges. Ideal for anyone looking to improve confidence, productivity, or career direction.',
		variants: [
			{
				id: '',
				manage_inventory: false,
				title: 'Individual coaching session',
				prices: [
					{
						amount: 6000,
						currency_code: 'USD',
						sale_amount: null,
						currency: {
							code: 'USD',
							decimal_digits: 2,
							template: '$$1',
							name: 'US Dollar',
							symbol: '$',
							symbol_native: '$',
							name_plural: 'US dollars',
							rounding: 0,
							min_amount: 0,
						},
					},
				],
				options: [],
				booking_event: {
					id: '',
					buffer_time_before: null,
					buffer_time_after: null,
					length: 3600000,
					length_unit: 'hours',
					location: 'Online',
					minimum_booking_notice: 600000,
					minimum_booking_notice_unit: 'minutes',
				},
			},
		],
	},
	{
		...DEFAULT_EMPTY_APPOINTMENT_VALUE,
		title: 'Group fitness class',
		images: [
			{
				url: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/fitness-group.png`,
			},
		],
		thumbnail: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/fitness-group.png`,
		subtitle: 'Energize your body and mind with a fun community workout',
		description: 'Join a small group session combining strength, mobility, and relaxation techniques. All fitness levels are welcome - bring your water bottle and positive energy.',
		variants: [
			{
				id: '',
				manage_inventory: false,
				title: 'Group fitness class',
				prices: [
					{
						amount: 1500,
						currency_code: 'USD',
						sale_amount: null,
						currency: {
							code: 'USD',
							decimal_digits: 2,
							template: '$$1',
							name: 'US Dollar',
							symbol: '$',
							symbol_native: '$',
							name_plural: 'US dollars',
							rounding: 0,
							min_amount: 0,
						},
					},
				],
				options: [],
				booking_event: {
					id: '',
					buffer_time_before: null,
					buffer_time_after: null,
					length: 3600000,
					length_unit: 'hours',
					location: 'Harmony Gym, 88 Maple Road',
					minimum_booking_notice: 900000,
					minimum_booking_notice_unit: 'minutes',
				},
			},
		],
	},
	{
		...DEFAULT_EMPTY_APPOINTMENT_VALUE,
		title: 'Creative course session',
		images: [
			{
				url: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/tutoring.png`,
			},
		],
		thumbnail: `${APPOINTMENT_PLACEHOLDERS_BASE_PATH}/tutoring.png`,
		subtitle: 'Explore new ideas and unlock your creative flow',
		description: 'A relaxed but focused idea-generation session for artists, entrepreneurs, or creators seeking inspiration. Together, we’ll explore fresh perspectives, refine your concepts, and build momentum toward your next big project.',
		variants: [
			{
				id: '',
				manage_inventory: false,
				title: 'Creative course session',
				prices: [
					{
						amount: 3500,
						currency_code: 'USD',
						sale_amount: null,
						currency: {
							code: 'USD',
							decimal_digits: 2,
							template: '$$1',
							name: 'US Dollar',
							symbol: '$',
							symbol_native: '$',
							name_plural: 'US dollars',
							rounding: 0,
							min_amount: 0,
						},
					},
				],
				options: [],
				booking_event: {
					id: '',
					minimum_booking_notice: 900000,
					minimum_booking_notice_unit: 'minutes',
					buffer_time_before: null,
					buffer_time_after: null,
					length: 2700000,
					length_unit: 'minutes',
					location: '12 Maple Street, Springfield, Illinois',
				},
			},
		],
	},
];

export const DEMO_PRODUCTS = [
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Classic cap',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/cap.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/cap.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Face Serum',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/bottle.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/bottle.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Handmade Vase',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/vase.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/vase.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Hand Soap',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/soap.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/soap.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Set of Plates',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/plates.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/plates.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Sunglasses',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/sunglasses.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/sunglasses.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Wooden Chair',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/chair.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/chair.png`,
	},
	{
		...DEFAULT_EMPTY_PRODUCT_VALUE,
		title: 'Wool Sweater',
		images: [
			{
				url: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/sweater.png`,
			},
		],
		thumbnail: `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/sweater.png`,
	},
	...DEMO_APPOINTMENTS,
];

export const BLANK_IMAGE_URL = `${ECOMMERCE_PRODUCT_PLACEHOLDERS_BASE_PATH}/blank.png`;

export const PRODUCT_TYPE_DONATION = 'donation';

export const BOOKING_DURATION_UNIT_HOURS = 'hours';

export const BLOCKS_ECOMMERCE = [
	'BlockEcommerceProduct',
	'BlockEcommerceProductList',
	'BlockAppointmentList',
	'BlockAppointment',
];
export const BLOCKS_APPOINTMENTS = [
	'BlockAppointmentList',
	'BlockAppointment',
];

export const CHECKOUT_ERROR_CODES = {
	INSUFFICIENT_INVENTORY: 'insufficient_inventory',
	BOOKING_UNAVAILABLE: 'booking_slot_not_available',
};

export const DEMO_ECOMMERCE_STORE_ID = 'demo_01G0E9P2R0CFTNBWEEFCEV8EG5';

export const SHOPPING_CART_STORAGE_KEY = 'shopping-cart-items';
export const SHOPPING_CART_TTL = 86400000; // 1 day
export const SITE_PRODUCT_SELECTION = 'site_product_selection';
export const SITE_PRODUCT_SELECTION_TYPE_LOWEST = 'lowest_price_first';
export const MAX_PRODUCTS_IN_CART = 250;

export const CATEGORY_ID_PREFIX = 'pcol_';

export const ECOMMERCE_SORTING_VALUES = {
	[SortingOptions.DEFAULT]: '',
	[SortingOptions.PRICE_LTH]: 'order=ASC&sort_by=price',
	[SortingOptions.PRICE_HTL]: 'order=DESC&sort_by=price',
	[SortingOptions.MOST_RECENT]: 'order=DESC&sort_by=created_at',
} as const;

export enum EcommerceProductMediaType {
	IMAGE = 'image',
	VIDEO = 'video',
}
export const ECOMERCE_PRODUCTS_FREE_TYPES = [
	EcommerceProductType.BOOKING,
	EcommerceProductType.DIGITAL,
];

export const ECOMMERCE_PAYMENT_STATUSES = {
	INVALID: 'invalid',
	CONNECTED: 'connected',
} as const;

export const STRIPE_JS_SDK_URL = 'https://js.stripe.com/v3/';

export const ECOMMERCE_PARCEL_MACHINE_PROVIDERS = [
	ECOMMERCE_SHIPPING_PROVIDER_TYPE.DPD,
	ECOMMERCE_SHIPPING_PROVIDER_TYPE.LP_EXPRESS,
	ECOMMERCE_SHIPPING_PROVIDER_TYPE.OMNIVA,
];

export const PRODUCT_TYPES_EXCLUDED_FROM_CART_SUGGESTIONS = [EcommerceProductType.BOOKING];
